import { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography } from '@mui/material';
import { Iconify } from 'src/components';
import {
  questionProgressContainerSx,
  questionProgressLabelSx,
  questionDotSx,
} from '../styles';

const QuestionProgress = ({
  questions = [],
  currentQuestionIndex = 0,
  completedQuestions = [],
  skippedQuestions = [],
  autoFilledQuestions = [],
  questionsWithNotes = [],
  conditionalQuestions = [],
  onQuestionClick,
  label = 'Questions:',
}) => {
  const containerRef = useRef(null);
  const activeRef = useRef(null);

  // Auto-scroll to keep the active pill visible
  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      const container = containerRef.current;
      const pill = activeRef.current;
      const { offsetLeft: pillLeft, offsetWidth: pillWidth } = pill;
      const { offsetWidth: containerWidth, scrollLeft } = container;

      // If pill is out of view on the right
      if (pillLeft + pillWidth > scrollLeft + containerWidth - 40) {
        container.scrollLeft = pillLeft - containerWidth / 2 + pillWidth / 2;
      }
      // If pill is out of view on the left
      if (pillLeft < scrollLeft + 60) {
        container.scrollLeft = pillLeft - 60;
      }
    }
  }, [currentQuestionIndex]);

  // Memoize status lookups with Sets for O(1) lookup
  const completedSet = useRef(new Set());
  const skippedSet = useRef(new Set());
  const autoFilledSet = useRef(new Set());
  const notesSet = useRef(new Set());
  const conditionalSet = useRef(new Set());

  completedSet.current = new Set(completedQuestions);
  skippedSet.current = new Set(skippedQuestions);
  autoFilledSet.current = new Set(autoFilledQuestions);
  notesSet.current = new Set(questionsWithNotes);
  conditionalSet.current = new Set(conditionalQuestions);

  const handleClick = useCallback((index) => {
    onQuestionClick?.(index);
  }, [onQuestionClick]);

  return (
    <Box ref={containerRef} sx={questionProgressContainerSx}>
      {label && (
        <Typography sx={questionProgressLabelSx}>{label}</Typography>
      )}
      {questions.map((question, index) => {
        const isActive = index === currentQuestionIndex;
        const isCompleted = completedSet.current.has(index);
        const isSkipped = skippedSet.current.has(index);
        const isAutoFilled = autoFilledSet.current.has(index);
        const hasNote = notesSet.current.has(index);
        const hasConditionalLogic = conditionalSet.current.has(index);

        const dot = (
          <Box
            key={question.id || index}
            ref={isActive ? activeRef : undefined}
            onClick={() => handleClick(index)}
            sx={questionDotSx(isActive, isCompleted, isSkipped, hasNote, isAutoFilled, hasConditionalLogic)}
          >
            {(() => {
              if (isCompleted) return <Iconify icon="mdi:check" width={12} height={12} />;
              if (isAutoFilled) return <Iconify icon="mdi:auto-fix" width={12} height={12} />;
              if (isSkipped) return <Iconify icon="mdi:redo" width={12} height={12} />;
              return index + 1;
            })()}
            {hasConditionalLogic && (
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  bottom: '-0.125rem',
                  right: '-0.125rem',
                  width: '0.875rem',
                  height: '0.875rem',
                  borderRadius: '50%',
                  bgcolor: '#7635DC',
                  border: '2px solid #fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Iconify icon="mdi:source-branch" width={8} height={8} sx={{ color: '#fff' }} />
              </Box>
            )}
          </Box>
        );

        if (hasConditionalLogic) {
          return (
            <Tooltip
              key={question.id || index}
              title="Has conditional logic"
              arrow
              placement="top"
            >
              {dot}
            </Tooltip>
          );
        }

        return dot;
      })}
    </Box>
  );
};

QuestionProgress.propTypes = {
  questions: PropTypes.array,
  currentQuestionIndex: PropTypes.number,
  completedQuestions: PropTypes.arrayOf(PropTypes.number),
  skippedQuestions: PropTypes.arrayOf(PropTypes.number),
  autoFilledQuestions: PropTypes.arrayOf(PropTypes.number),
  questionsWithNotes: PropTypes.arrayOf(PropTypes.number),
  conditionalQuestions: PropTypes.arrayOf(PropTypes.number),
  onQuestionClick: PropTypes.func,
  label: PropTypes.string,
};

export default QuestionProgress;
